"use client";

import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import CustomerSlaOverrideService, {
  CustomerSlaOverride,
} from "../services/customer-sla-override-service";
import {
  SLA_APPLIES_LABEL,
  SLA_MILESTONES_BY_APPLY,
  SlaAppliesTo,
} from "../../setting/fault-sla-config";

interface Props {
  customerId: number;
  customerName: string;
  onClose: () => void;
  /** Báo cho danh sách khách biết khách này còn override nào không, để cập nhật cột "SLA riêng" ngay mà không phải tra lại cả bảng. */
  onSaved?: (customerId: number, hasOverride: boolean) => void;
}

const GROUP_ACCENT: Record<SlaAppliesTo, string> = {
  NORMAL: "border-blue-200 bg-blue-50 text-blue-700",
  COUNTING: "border-amber-200 bg-amber-50 text-amber-700",
};

/**
 * Cấu hình SLA riêng cho MỘT khách hàng: mỗi mốc 1 ô giờ, để trống = dùng mốc CHUNG (Cài đặt hệ thống).
 * Không sửa điểm phạt/bộ phận ở đây — hai thứ đó vẫn theo cấu hình chung, chỉ thời gian xử lý là riêng.
 */
const CustomerSlaConfigModal: React.FC<Props> = ({ customerId, customerName, onClose, onSaved }) => {
  // Khoá là milestone_code; giá trị là CHUỖI người dùng đang gõ (để trống hợp lệ, khác 0).
  const [hoursByCode, setHoursByCode] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  const { fetch: fetchOverrides, loading: loadingList } = CustomerSlaOverrideService.list();
  const { fetch: saveOverrides, loading: saving } = CustomerSlaOverrideService.save();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchOverrides(customerId);
      if (cancelled) return;
      if (res && isSuccess(res)) {
        const map: Record<string, string> = {};
        for (const o of (res.data ?? []) as CustomerSlaOverride[]) {
          map[o.milestone_code] = String(o.hours);
        }
        setHoursByCode(map);
      } else {
        showToast({
          message: res?.message ?? "Không tải được cấu hình SLA riêng của khách",
          type: "error",
        });
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleSave = async () => {
    const overrides: CustomerSlaOverride[] = Object.entries(hoursByCode).map(([code, v]) => ({
      milestone_code: code,
      hours: Number(v) || 0, // 0/rỗng → BE hiểu là xoá override, quay lại dùng mốc chung
    }));

    const res = await saveOverrides(customerId, overrides);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã lưu cấu hình SLA riêng cho khách", type: "success" });
      onSaved?.(customerId, overrides.some((o) => o.hours > 0));
      onClose();
    } else {
      showToast({ message: res?.message ?? "Không lưu được cấu hình SLA", type: "error" });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-2xl max-h-[90vh] flex-col rounded-xl bg-white shadow-xl">
        {/* HEADER */}
        <div className="flex shrink-0 items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-700">Cấu hình SLA riêng</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Khách hàng: <strong className="text-slate-700">{customerName}</strong>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 space-y-5 overflow-y-auto p-6">
          <div className="rounded-lg border bg-slate-50 p-3 text-sm text-slate-600">
            Mốc nào <strong>để trống</strong> sẽ dùng thời gian <strong>chung</strong> đang cấu hình ở
            Cài đặt hệ thống. Chỉ thời gian xử lý là riêng — điểm phạt và bộ phận chịu trách nhiệm ở mỗi
            lần leo thang vẫn theo cấu hình chung.
          </div>

          {!loaded || loadingList ? (
            <div className="py-8 text-center text-slate-400">Đang tải…</div>
          ) : (
            SLA_MILESTONES_BY_APPLY.map((group) => (
              <div key={group.applies} className="space-y-2">
                <div
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold ${GROUP_ACCENT[group.applies]}`}
                >
                  {SLA_APPLIES_LABEL[group.applies]}
                </div>
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {group.milestones.map((m) => (
                    <div
                      key={m.code}
                      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700">{m.name}</div>
                        <div className="mt-0.5 text-xs text-slate-400">
                          {m.from} → {m.to} · mặc định {m.defaultHours}h
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          placeholder={`${m.defaultHours}`}
                          value={hoursByCode[m.code] ?? ""}
                          onChange={(e) =>
                            setHoursByCode((prev) => ({ ...prev, [m.code]: e.target.value }))
                          }
                          className="w-20 rounded-lg border px-3 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                        <span className="text-xs text-slate-500">giờ</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-slate-600 hover:bg-slate-50"
          >
            Huỷ
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!loaded || saving}
            className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {saving ? "Đang lưu…" : "Lưu cấu hình"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSlaConfigModal;
